import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, ArrowRight } from 'lucide-react';

const Pricing = () => {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Starter',
      price: '$99',
      period: 'per month',
      description: 'Perfect for small practices getting started',
      features: [
        'Up to 50 referrals/month',
        '3 user accounts',
        'Basic referral tracking',
        'Email support',
        'Mobile app access',
        'HIPAA compliant',
      ],
      cta: 'Start Free Trial',
      highlighted: false,
    },
    {
      name: 'Professional',
      price: '$299',
      period: 'per month',
      description: 'For growing practices with advanced needs',
      features: [
        'Unlimited referrals',
        'Unlimited users',
        'Advanced analytics dashboard',
        'Deal rooms & file sharing',
        'AI-powered chat',
        'Priority support',
        'Custom integrations',
        'Dedicated account manager',
      ],
      cta: 'Get Started',
      highlighted: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact sales',
      description: 'For large organizations with custom requirements',
      features: [
        'Everything in Professional',
        'Custom development',
        'White-label options',
        'Multi-location support',
        'Advanced security features',
        'SLA guarantees',
        'Dedicated implementation team',
        '24/7 premium support',
      ],
      cta: 'Contact Sales',
      highlighted: false,
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0A1628] to-[#2d3e50] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Choose the plan that fits your practice. All plans include a 14-day free trial.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 -mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative ${
                  plan.highlighted
                    ? 'border-[#D4AF37] border-2 shadow-2xl transform scale-105'
                    : 'border-gray-200 shadow-lg'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#D4AF37] text-[#0A1628] px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <CardHeader className="text-center pb-8">
                  <CardTitle className="text-2xl font-bold text-[#0A1628] mb-2">
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-[#0A1628]">{plan.price}</span>
                    {plan.period !== 'contact sales' && (
                      <span className="text-gray-600 ml-2">/{plan.period.split(' ')[1]}</span>
                    )}
                  </div>
                  <CardDescription className="mt-4 text-base">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <Button
                    className={`w-full mb-6 ${
                      plan.highlighted
                        ? 'bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]'
                        : 'bg-[#0A1628] text-white hover:bg-[#1a2332]'
                    }`}
                    size="lg"
                    onClick={() => plan.name === 'Enterprise' ? navigate('/contact') : navigate('/register')}
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <div className="space-y-3">
                    {plan.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-[#D4AF37] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0A1628] mb-12">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0A1628] mb-2">
                  Can I change plans at any time?
                </h3>
                <p className="text-gray-600">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0A1628] mb-2">
                  What payment methods do you accept?
                </h3>
                <p className="text-gray-600">
                  We accept all major credit cards (Visa, MasterCard, American Express) and ACH bank transfers for annual plans.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0A1628] mb-2">
                  Is there a long-term contract?
                </h3>
                <p className="text-gray-600">
                  No! All plans are month-to-month with no long-term commitment. You can cancel anytime.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-[#0A1628] mb-2">
                  Do you offer discounts for annual billing?
                </h3>
                <p className="text-gray-600">
                  Yes! Save 20% when you pay annually. Contact our sales team for custom enterprise pricing.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] to-[#2d3e50] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Our team is here to help you find the perfect plan for your practice.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B] text-lg px-8"
          >
            Contact Sales Team
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
